/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { BackendRoutes } from '../../constants'
import { ConfigService } from '../config/config.service'
import { UntypedFormGroup } from '@angular/forms'

export type LayoutSchemaType =
  | 'prospect-registration'
  | 'prospect-confirmation'
  | 'organisation-registration'
  | 'owner-confirmation'
  | 'secondary-confirmation'
  | 'track-your-dream-confirmation'

interface LegalConditionLink {
  type: string
  linkPDF?: string
  linkExt?: string
  ciamVersion?: string
}

// Named label placeholders supported in addition to the generic `{{link:<TYPE>}}` syntax.
const NAMED_LINK_PLACEHOLDERS: Record<string, string> = {
  DPG: '{{dpgLink}}',
  DPP_CONNECT: '{{dppConnectLink}}',
}

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private _httpClient = inject(HttpClient)
  private _configService = inject(ConfigService)

  public getSchema(country: string, locale: string, type: LayoutSchemaType = 'prospect-registration'): Observable<any> {
    const path = BackendRoutes.FORM__SCHEMA__SIGNUP.replace(':type', type)
      .replace(':country', country.toLowerCase())
      .replace(':locale', locale)
      .replace(':version', this._configService.isProduction() ? 'RELEASE' : 'LATEST')
    return this._httpClient.get(`${this._configService.getLocalisationBaseUrl()}${path}`)
  }

  public getLayout(country: string, type: LayoutSchemaType = 'prospect-registration'): Observable<any> {
    const path = BackendRoutes.FORM__LAYOUT__SIGNUP.replace(':type', type)
      .replace(':country', country.toLowerCase())
      .replace(':stage', this._configService.isProduction() ? 'p' : 'pp')
      .replace(':version', this._configService.isProduction() ? 'RELEASE' : 'LATEST')

    return this._httpClient.get(`${this._configService.getLocalisationBaseUrl()}${path}`)
  }

  public extendSchemaWithLegalConditionLinks(schema: any[], legalConditions: LegalConditionLink[] = []): any {
    return schema.map((schemaItem: any) => {
      if (Array.isArray(schemaItem?.group)) {
        schemaItem.group = this.extendSchemaWithLegalConditionLinks(schemaItem.group, legalConditions)
      }
      if (FormService.hasLegalConditions(schemaItem)) {
        return FormService.replaceGenericLegalConditionLinks(schemaItem, legalConditions)
      }
      switch (schemaItem.id) {
        case 'tac':
          schemaItem.label = schemaItem.label.replace(
            /{{tacLink}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'TAC')?.linkPDF ?? '',
          )
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'TAC')?.linkPDF ?? '', // replace TAC linkExt does not exist
          )
          break
        case 'dpg':
          schemaItem.label = schemaItem.label.replace(
            /{{dpgLink}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG')?.linkExt ?? '',
          )
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG')?.linkExt ?? '',
          )
          break
        case 'dpg_2':
          schemaItem.label = schemaItem.label.replace(
            /{{dpgLink}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG_2')?.linkExt ?? '',
          )
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG_2')?.linkExt ?? '',
          )
          break
        case 'dpg_3':
          schemaItem.label = schemaItem.label.replace(
            /{{dpgLink}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG_3')?.linkExt ?? '',
          )
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG_3')?.linkExt ?? '',
          )
          break
        case 'legalText':
          schemaItem.label = schemaItem.label
            .replace(/{{tacLink}}/, legalConditions.find((lc: LegalConditionLink) => lc.type === 'TAC')?.linkPDF ?? '') // replace TAC linkExt does not exist
            .replace(/{{touLink}}/, legalConditions.find((lc: LegalConditionLink) => lc.type === 'TOU')?.linkPDF ?? '') // replace TOU linkExt does not exist
            .replace(/{{dpgLink}}/, legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG')?.linkExt ?? '')
          break
        case 'thirdPartyTransferCheck':
          schemaItem.label = schemaItem.label.replace(
            /{{dpgLink}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG')?.linkExt ?? '',
          )
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc: LegalConditionLink) => lc.type === 'DPG')?.linkExt ?? '',
          )
          break
        case 'tou_lbs':
          schemaItem.label = schemaItem.label.replace(/{{touLbsLink}}/, legalConditions.find((lc) => lc.type === 'TOU_LBS')?.linkExt ?? '')
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc) => lc.type === 'TOU_LBS')?.linkExt ?? '',
          )
          break
        case 'tou_lib':
          schemaItem.label = schemaItem.label.replace(/{{touLibLink}}/, legalConditions.find((lc) => lc.type === 'TOU_LIB')?.linkExt ?? '')
          schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(
            /{{link}}/,
            legalConditions.find((lc) => lc.type === 'TOU_LIB')?.linkExt ?? '',
          )
          break
      }
      return schemaItem
    })
  }

  // Structure the data correctly so that the api can make sense of it
  public prepareFormPayload(formGroup: UntypedFormGroup, legalConditions: LegalConditionLink[] = [], schema: any[] = []): any {
    const result: any = {}
    const legalConditionsByControlId = FormService.collectLegalConditionsByControlId(schema)

    const applyLegalConditions = (ctrlKey: string, value: unknown): void => {
      const types = legalConditionsByControlId.get(ctrlKey)
      if (value !== true || types === undefined) {
        return
      }
      // Init legal prop lazily in case they are required but not defined yet
      if (result.legal === null || result.legal === undefined) {
        result.legal = {}
      }
      types.forEach((type) => {
        result.legal[type.toLowerCase()] = legalConditions.find((lc: LegalConditionLink) => lc.type === type)?.ciamVersion
      })
    }

    Object.entries(formGroup.controls).forEach(([ctrlKey, ctrlValue]) => {
      if (ctrlValue instanceof UntypedFormGroup) {
        Object.entries(ctrlValue.value).forEach(([key, value]) => {
          result[key] = value
          applyLegalConditions(key, value)
        })
      } else if (legalConditionsByControlId.has(ctrlKey)) {
        applyLegalConditions(ctrlKey, ctrlValue.value)
        result[ctrlKey] = ctrlValue.value
      } else {
        switch (ctrlKey) {
          case 'select_all':
            // UI-only helper control; never send it to the backend.
            break
          case 'tac':
          case 'dpg':
          case 'dpg_2':
          case 'dpg_3':
            if (ctrlValue?.value === true) {
              // Init legal prop lazily in case they are required but not defined yet
              if (result.legal === null || result.legal === undefined) {
                result.legal = {}
              }
              result.legal[ctrlKey] = legalConditions.find((lc: LegalConditionLink) => lc.type === ctrlKey.toUpperCase())?.ciamVersion
            }
            break
          default:
            result[ctrlKey] = ctrlValue.value
        }
      }
    })
    return result
  }

  private static hasLegalConditions(schemaItem: any): boolean {
    return Array.isArray(schemaItem?.legalConditions) && schemaItem.legalConditions.length > 0
  }

  private static legalConditionLink(legalConditions: LegalConditionLink[], type: string): string {
    const legalCondition = legalConditions.find((lc: LegalConditionLink) => lc.type === type)
    return legalCondition?.linkExt ?? legalCondition?.linkPDF ?? ''
  }

  private static replaceGenericLegalConditionLinks(schemaItem: any, legalConditions: LegalConditionLink[]): any {
    const types: string[] = schemaItem.legalConditions
    if (typeof schemaItem.label === 'string') {
      schemaItem.label = types.reduce((label: string, type: string) => {
        const link = FormService.legalConditionLink(legalConditions, type)
        const namedPlaceholder = NAMED_LINK_PLACEHOLDERS[type]
        const replaced = label.split(`{{link:${type}}}`).join(link)
        return namedPlaceholder ? replaced.split(namedPlaceholder).join(link) : replaced
      }, schemaItem.label)
    }
    schemaItem.openURLOnClick = schemaItem.openURLOnClick?.replace(/{{link}}/, FormService.legalConditionLink(legalConditions, types[0]))
    return schemaItem
  }

  private static collectLegalConditionsByControlId(schema: any[] = [], result = new Map<string, string[]>()): Map<string, string[]> {
    for (const schemaItem of schema ?? []) {
      if (FormService.hasLegalConditions(schemaItem) && typeof schemaItem.id === 'string') {
        result.set(schemaItem.id, schemaItem.legalConditions)
      }
      if (Array.isArray(schemaItem?.group)) {
        FormService.collectLegalConditionsByControlId(schemaItem.group, result)
      }
    }
    return result
  }
}
